import React, { useEffect, useRef, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Typography,
  useTheme,
  Box,
  Pagination,
  useMediaQuery,
  CardContent,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Stack,
  Skeleton,
  SxProps,
  Theme,
  ButtonBase,
} from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Table from "@mui/material/Table";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { tokens } from "../../theme";
import { IVR_STATUS, PATIENT_STATUS } from "../../constants/applicationConstants";
import styled from "styled-components";
import Card from "./Card";
import { ArrowUpward, ArrowUpwardOutlined, ImportExportOutlined, RadioButtonChecked } from "@mui/icons-material";
import Button from "./Button";
import { getSelectionForPage } from "../../utils/StorageUtils";

interface Column {
  id: string;
  label: string | JSX.Element;
  minWidth?: number;
  width?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
  padding?: string;
}

export interface Row {
  [key: string]: any;
  uniqueId?: any;
}

interface Props {
  columns: Column[];
  rows: Row[];
  hiddenColumns?: string[];
  onRowClick?: (row: Row) => void;
  actionComponent?: (row: any, rowIndex: number) => JSX.Element | null;
  disablePagination?: boolean;
  borderBox?: boolean;
  emptyText?: string;
  pageNumber?: (newPage: number) => void;
  totalPages?: number;
  totalElementText?: string;
  resetPage?: boolean;
  currentPageNumber?: number;
  uniqueValue?: string;
  cardContentComponent?: (row: Row) => JSX.Element;
  disabled?: boolean;
  availabilitySwitchComponent?: (row: any, rowIndex: number) => JSX.Element;
  showMenu?: boolean;
  visibleHeader?: boolean;
  stickyColumns?: string[];
  sortableColumns?: string[];
  // Task note: pages that need DB/API sorting can listen here and trigger their own fetch.
  onSortChange?: (columnId: string, order: "asc" | "desc") => void;
  // Task note: when true, keep header sort UI in this shared table but let the page own the sorted data.
  serverSideSorting?: boolean;
  controlledSortColumn?: string | null;
  controlledSortOrder?: "asc" | "desc" | null;
  singleActionComponent?: JSX.Element;
  //CS-1071
  activeButtonId?: string|null;
  currentPageKey?: string|null;
  //
  //CS-1297 - Start
  showPverifySkeleton?:boolean
  sx?: SxProps<Theme>;
  // CS-1555 - Start
  disableRowClickOnCards?: boolean;
  clickableCards?: boolean;
  openMenuOnRowClick?: boolean;
  // CS-1555 - End
}

const CustomTable: React.FC<Props> = ({
  columns,
  rows,
  hiddenColumns = [],
  onRowClick,
  actionComponent,
  disablePagination,
  borderBox,
  emptyText,
  pageNumber,
  totalPages,
  totalElementText,
  resetPage,
  currentPageNumber,
  uniqueValue,
  cardContentComponent,
  disabled,
  availabilitySwitchComponent,
  showMenu,
  visibleHeader = true,
  stickyColumns,
  sortableColumns,
  onSortChange,
  serverSideSorting = false,
  controlledSortColumn,
  controlledSortOrder,
  singleActionComponent,
  //CS-1071
  activeButtonId,
  currentPageKey,
  showPverifySkeleton,
    //
  sx,
  // CS-1555 - Start
  disableRowClickOnCards = false,
  clickableCards = false,
  openMenuOnRowClick = false,
  // CS-1555 - End
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const colors = tokens(theme.palette.mode);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRow, setSelectedRow] = useState<Row | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null); // CS-1555
  const [menuRow, setMenuRow] = useState<Row | null>(null);
  const [menuActionPropRow, setMenuActionPropRow] = useState<any | null>(null);
  const [isActionProps, setIsActionProps] = useState(false);
  const [activeMenuRow, setActiveMenuRow] = useState<Row | null>(null);

  // Sticky cell hover color is driven by this state (a real React re-render) instead of a
  // CSS ":hover" pseudo-class, because Chrome is slow to repaint a position:sticky
  // element's background when it's only triggered by an ancestor's :hover — it visibly
  // lags a second or two behind the rest of the row. A state-driven style update repaints
  // immediately, so both stay in sync.
  const [hoveredRowKey, setHoveredRowKey] = useState<string | number | null>(null);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [sortedRows, setSortedRows] = useState<Row[]>(rows);
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  const [mobileSortAnchor, setMobileSortAnchor] = useState<null | HTMLElement>(null);
  const [mobileSortColumn, setMobileSortColumn] = useState<string | null>(null);

  useEffect(() => {
    setPage(0);
  }, [resetPage]);

  useEffect(() => {
    if (currentPageNumber !== null && currentPageNumber !== undefined) {
      setPage(currentPageNumber);
    }
  }, [currentPageNumber]);

  useEffect(() => {
    if (controlledSortColumn === undefined && controlledSortOrder === undefined) {
      return;
    }

    setSortColumn(controlledSortColumn ?? null);
    setSortOrder(controlledSortOrder ?? "asc");
    setMobileSortColumn(controlledSortColumn ?? null);
  }, [controlledSortColumn, controlledSortOrder]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    pageNumber && pageNumber(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRowClick = (row: Row) => {
    setSelectedRow(row);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: Row) => {
    event.stopPropagation(); // CS-1555
    setAnchorEl(event.currentTarget);
    setMenuPosition({ top: event.clientY, left: event.clientX }); // CS-1555
    setMenuRow(row);
    setActiveMenuRow(row);
    setIsActionProps(true);
  };

  const handleWithoutPropMenuClick = (event: React.MouseEvent<HTMLElement>, rowColumn: any, row: Row) => {
    event.stopPropagation(); // CS-1555
    setAnchorEl(event.currentTarget);
    setMenuPosition({ top: event.clientY, left: event.clientX }); // CS-1555
    setMenuActionPropRow(rowColumn);
    setActiveMenuRow(row);
    setIsActionProps(false);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
    setActiveMenuRow(null);
    setMenuPosition(null); // CS-1555
  };

  const filteredColumns = columns.filter(
    (column) => !hiddenColumns.includes(column.id)
  );

  // Sticky columns are pinned to the right edge of the table so they stay visible
  // when the table scrolls horizontally (e.g. the Action column on wide list views).
  const STICKY_COLUMN_FALLBACK_WIDTH = 90;
  
  const stickyColumnIds =
    stickyColumns ?? (filteredColumns.some((c) => c.id === "action") ? ["action"] : []);
  const [stickyMeasuredWidths, setStickyMeasuredWidths] = useState<Record<string, number>>({});
  const stickyHeaderRefs = useRef<Record<string, HTMLTableCellElement | null>>({});

  useEffect(() => {
    if (stickyColumnIds.length === 0) return undefined;
    const measure = () => {
      setStickyMeasuredWidths((prev) => {
        let changed = false;
        const next = { ...prev };
        stickyColumnIds.forEach((id) => {
          const el = stickyHeaderRefs.current[id];
          const width = el?.getBoundingClientRect().width;
          if (width && Math.abs((next[id] ?? 0) - width) > 0.5) {
            next[id] = width;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    Object.values(stickyHeaderRefs.current).forEach((el) => el && observer.observe(el));
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stickyColumnIds.join(","), filteredColumns.length]);

  const getStickyRightOffset = (columnId: string) => {
    const idx = filteredColumns.findIndex((c) => c.id === columnId);
    if (idx === -1) return 0;
    let offset = 0;
    for (let i = idx + 1; i < filteredColumns.length; i++) {
      const col = filteredColumns[i];
      if (stickyColumnIds.includes(col.id)) {
        offset += stickyMeasuredWidths[col.id] ?? col.width ?? col.minWidth ?? STICKY_COLUMN_FALLBACK_WIDTH;
      }
    }
    return offset;
  };
  const getStickyCellSx = (columnId: string, isHeader: boolean) => {
    if (!stickyColumnIds.includes(columnId)) return {};
    return {
      position: "sticky" as const,
      right: getStickyRightOffset(columnId),
      zIndex: isHeader ? 3 : 1,
      willChange: "background-color",
    };
  };

  // CS-1517 - Start
  useEffect(() => {
    if (serverSideSorting) {
      setSortedRows([...rows]);
      return;
    }
    if (sortColumn && sortableColumns?.includes(sortColumn)) {
      const sorted = [...rows].sort((a, b) => {
        const rawColumnId = `raw${sortColumn.charAt(0).toUpperCase()}${sortColumn.slice(1)}`;
        const valA = a[rawColumnId] ?? a[sortColumn!] ?? "";
        const valB = b[rawColumnId] ?? b[sortColumn!] ?? "";
        // Always push empty/null values to the bottom regardless of sort direction
        if (valA === "" && valB !== "") return 1;
        if (valA !== "" && valB === "") return -1;
        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
      setSortedRows(sorted);
    } else {
      setSortedRows([...rows]);
    }
  }, [rows, sortColumn, sortOrder, sortableColumns, serverSideSorting]);
  // CS-1517 - End

  // CS-1517 - Start
  const handleSort = (columnId: string) => {
    if (!sortableColumns?.includes(columnId)) return;
  
    const isAsc = sortColumn === columnId && sortOrder === "asc";
    const newOrder = isAsc ? "desc" : "asc";

    setSortColumn(columnId);
    setSortOrder(newOrder);

    if (serverSideSorting) {
      setSortedRows([...rows]);
      // Task note: this is the hand-off point for module-specific API sorting.
      onSortChange?.(columnId, newOrder);
      return;
    }
  
    const sorted = [...rows].sort((a, b) => {
      const rawColumnId = `raw${columnId.charAt(0).toUpperCase()}${columnId.slice(1)}`;
      const valA = a[rawColumnId] ?? a[columnId] ?? '';
      const valB = b[rawColumnId] ?? b[columnId] ?? '';
      // Always push empty/null values to the bottom regardless of sort direction
      if (valA === "" && valB !== "") return 1;
      if (valA !== "" && valB === "") return -1;
      if (valA < valB) return newOrder === "asc" ? -1 : 1;
      if (valA > valB) return newOrder === "asc" ? 1 : -1;
      return 0;
    });
  
    setSortedRows(sorted);
    onSortChange?.(columnId, newOrder);
  };
  // CS-1517 - End
  
  const activeSortColor = theme.palette.mode === "dark" ? "#3bf1dc" : "#10635c";

  const renderSortIndicator = (columnId: string) => {
    const isActive = sortColumn === columnId;
    const isHovered = hoveredColumn === columnId;
    const isDark = theme.palette.mode === "dark";
    const idleColor = isDark ? colors.grey[500] : colors.grey[400];
    const iconColor = isActive || isHovered ? activeSortColor : idleColor;
    const SortIcon = isActive ? ArrowUpward : ArrowUpwardOutlined;

    return (
      <IconButton
        size="small"
        sx={{
          color: iconColor,
          transition: "color 0.2s ease-in-out",
          "&:hover": { opacity: 1 },
        }}
      >
        <SortIcon
          sx={{
            fontSize: isActive ? 18 : 16,
            opacity: isActive ? 1 : isHovered ? 0.85 : 0.5,
            transition: "opacity 0.2s, transform 0.3s ease-in-out",
            transform: isActive && sortOrder === "asc" ? "rotate(0deg)" : "rotate(180deg)",
            ...(isActive ? { stroke: iconColor, strokeWidth: 0.6 } : {}),
          }}
        />
      </IconButton>
    );
  };

  const handleMobileSortMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileSortAnchor(event.currentTarget);
  };

  const handleMobileSortMenuClose = () => {
    setMobileSortAnchor(null);
  };

  const handleMobileSort = (columnId: string) => {
    setMobileSortColumn(columnId);
    handleSort(columnId);
    setMobileSortAnchor(null);
  };

  
  const renderMobileSortMenu = () => (
    <Menu
      anchorEl={mobileSortAnchor}
      open={Boolean(mobileSortAnchor)}
      onClose={handleMobileSortMenuClose}
      sx={{ zIndex: 1300 }}
    >
      {sortableColumns?.map((columnId) => {
        const column = columns.find((col) => col.id === columnId);
        return (
          <MenuItem
            key={columnId}
            onClick={() => handleMobileSort(columnId)}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography>{column?.label}</Typography>
            {mobileSortColumn === columnId && (
              <Box>
                {sortOrder === "asc" ? (
                  <ArrowUpward sx={{ fontSize: 18, color: activeSortColor }} />
                ) : (
                  <ArrowUpward sx={{ fontSize: 18, color: activeSortColor, transform: "rotate(180deg)" }} />
                )}
              </Box>
            )}
          </MenuItem>

        );
      })}
    </Menu>
  );

  const renderSingleActionComponent = () => {
    if (!singleActionComponent) return null;
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        {singleActionComponent}
      </Box>
    );
  };

  const renderDynamicTableSize = (data:any) =>{
    if(data === "Billing"){
      return 300;
    }else if(data==="CreateInvoice"){
      return 570;
    }else{
      return "auto";
    }
  }

  const renderTable = () => (
    <Box sx={{ width: "100%", overflowX: isMobile ? "auto" : "hidden", mt: 1 }}>
      {isMobile && sortableColumns && sortableColumns.length > 0 && (
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="text"
          startIcon={<ImportExportOutlined />}
          onClick={handleMobileSortMenuOpen}
            >
          Sort
        </Button>
      </Box>
      )}
      {sortableColumns && sortableColumns.length > 0 && renderMobileSortMenu()}
      
      {(!isMobile) && (
        <TableContainer
          sx={{
            // maxHeight: uniqueValue==="Billing" ? 300 : "auto", // adjust height to fit approx 5 rows
            maxHeight: renderDynamicTableSize(uniqueValue), // adjust height to fit approx 5 rows
            overflowY: uniqueValue==="Billing" ? "auto" : "auto",
          }}>
          <Table
            stickyHeader
            aria-label="sticky table"
            style={{ background: disabled ? theme.palette.mode === "dark" ? "#383838" : "#FFFFFF" :  colors.grey[800] }}
          >
            {visibleHeader && (
            <TableHead>
              <TableRow>
                {filteredColumns.map(
                  (column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      ref={(el: HTMLTableCellElement | null) => {
                        if (stickyColumnIds.includes(column.id)) stickyHeaderRefs.current[column.id] = el;
                      }}
                      style={{
                        minWidth: column.minWidth, width: column.width, background: disabled ? theme.palette.mode === "dark" ? "#383838" : "#FFFFFF" :  colors.grey[800],
                         height : 40, paddingTop : 0, paddingBottom : 0,
                        ...(column.padding && { padding: column.padding })
                      }}
                      onClick={() =>
                        sortableColumns?.includes(column.id) && handleSort(column.id)
                      }
                      sx={{
                        cursor: sortableColumns?.includes(column.id) ? "pointer" : "default",
                        ...getStickyCellSx(column.id, true),
                      }}
                      onMouseEnter={() => setHoveredColumn(column.id)}
                      onMouseLeave={() => setHoveredColumn(null)}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: column.align === "right" ? "flex-end" : "flex-start",
                          gap: "8px",
                        }}
                      >
                        <Typography
                          fontWeight="bold"
                          color={
                            disabled
                              ? "#898989"
                              : theme.palette.mode === "dark"
                              ? colors.info[300]
                              : colors.grey[100] // CS-854 changes 
                          }
                          sx={{
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                          }} 
                        >
                          {column.label}
                        </Typography>
                        {sortableColumns?.includes(column.id) && (
                          <Box>
                            {renderSortIndicator(column.id)}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            )}
            <TableBody>
              {rows.length > 0 ? (
                sortedRows.map((row, rowIndex) => renderTableRow(row, rowIndex))
              ) : (
                <TableRow>
                  <TableCell colSpan={filteredColumns.length + 1} align="center">
                    {/* CS-1297 - Start */}
                    {showPverifySkeleton ? (
                      <Skeleton variant="rectangular"  animation="wave" />
                    ):(
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                      {emptyText || "No Data Found"}
                    </Typography>
                    )}
                    {/* CS-1297 - End */}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {isMobile && renderMobileCards()}
      {/* {isMobile && (
        <Box>
          {rows.length > 0 ? (
            rows.map((row, rowIndex) => renderCardView(row, rowIndex))
          ) : (
            <Box sx={{ textAlign: "center", padding: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                {emptyText || "No Data Found"}
              </Typography>
            </Box>
          )}
        </Box>
      )} */}
      {renderSingleActionComponent()}
      {!disablePagination && renderPagination()}
    </Box>
  );

  const renderTableRow = (row: Row, rowIndex: number) => {
    const isDrafted = row.status === IVR_STATUS.DRAFTED;
    const isVerified = row.status === IVR_STATUS.VERIFIED;
    const isCancelled = row.status === IVR_STATUS.VOID;
    const isVoid = row.status === IVR_STATUS.CANCELED;
    const isDenied = row.status === IVR_STATUS.DENIED;
    const isNeedMoreInfo = row.status === IVR_STATUS.NEEDS_MORE_INFO;
    const isSelected = activeMenuRow === row;
    const isPatientActive = row.status === PATIENT_STATUS.ACTIVE ;
    const isPatientClosed = row.status === PATIENT_STATUS.CLOSED ;
    const isInactive = row.status === "INACTIVE";
    const isUserActive = row.userStatus === "ACTIVE";
    const isPreAuth = row.status === IVR_STATUS.PRE_AUTH;

    const isActiveMenuRow = activeMenuRow && uniqueValue && activeMenuRow[uniqueValue] === row[uniqueValue];
    //CS-1071 - Start
    // const isHighlightedRow = (() => {
    //   if (!activeButtonId || !uniqueValue) return false;
    //   const rowId = String(row[uniqueValue]);
    //   const activeId = activeButtonId.split("-").pop(); // Last segment
    //   return rowId === activeId;
    // })();
    const isHighlightedRow = (() => {
      if (!uniqueValue) return false; // Pass page-specific key here, if dynamic
      const activeIdForPage = getSelectionForPage(currentPageKey || "default"); 
      if (!activeIdForPage) return false;
    
      const rowId = String(row[uniqueValue]);
      const activeId = activeIdForPage.split("-").pop(); // Last segment
      return rowId === activeId;
    })();
    
    //End
    const hasActionComponent = actionComponent && actionComponent(row, rowIndex) !== null;

    // CS-1555 - Start
    const handleTableRowClick = (event: React.MouseEvent<HTMLTableRowElement>, row: Row) => {
      const target = event.target as HTMLElement;
      const isInteractive =
        !!target.closest('button, a, input, select, [role="button"]');
      if (isInteractive) return;
      // Open action menu on row click if enabled
      if (openMenuOnRowClick && showMenu && actionComponent) {
        handleMenuClick(event as unknown as React.MouseEvent<HTMLElement>, row);
        return;
      }
      if (onRowClick) {
        handleRowClick(row);
        onRowClick(row);
      }
    };
    
    const rowKey = row.uniqueId ?? (uniqueValue ? row[uniqueValue] : rowIndex);
    const isRowHovered = hoveredRowKey === rowKey;
    const stickyHoverBg = theme.palette.mode === "dark" ? "#162A30" : "#E6F2F2";
    const stickyCellBg = isHighlightedRow
      ? colors.grey[600]
      : isSelected || isActiveMenuRow
        ? colors.grey[700]
        : isRowHovered
          ? stickyHoverBg
          :  colors.grey[800];

    const rowHoverHandlers = {
      onMouseEnter: () => setHoveredRowKey(rowKey),
      onMouseLeave: () => setHoveredRowKey((k) => (k === rowKey ? null : k)),
    };

    const commonRowSx = {
      cursor: onRowClick ? "pointer" : "",
      background: isHighlightedRow
        ? colors.grey[600]
        : isSelected || isActiveMenuRow
          ? colors.grey[700]
          : "inherit",
      "&:hover": { backgroundColor: colors.grey[700] },
      color: "inherit",
      "& td": { color: "inherit" },
    } as const;

    const enableRipple = !isMobile && !!onRowClick && !!clickableCards;
    // CS-1555 - End

    const renderOrderStatus = () => {
      const orderStatus = row.orderStatus;
    
      if (typeof orderStatus === "string") {
        return (
          <span>
            {orderStatus === "DELIVERED" ? (
              <span style={{ color: "#28a745" }}>{orderStatus}</span>
            ) : orderStatus === "SHIPPED" ? (
              <span style={{ color: "#5dabff" }}>{orderStatus}</span>
            ) : orderStatus === "CANCELED" ? (
              <span style={{ color: "#dc3545" }}>{orderStatus}</span>
            ) : orderStatus === "PROCESSING" ? (
              <span style={{ color: "#fd7e14" }}>{orderStatus}</span>
            ) : orderStatus === "PLACED" ? (
              <span style={{ color: theme.palette.text.primary }}>{orderStatus}</span>
            ) : (
                orderStatus
              )}
          </span>
        );
      }
    
      const orderStatusChildren = orderStatus?.props?.children;
      if (!orderStatusChildren || !Array.isArray(orderStatusChildren)) return null;

  return (
    <Box display="flex" alignItems="center" gap={1}>
          {orderStatusChildren.map((child: any, index: number) => {
          if (child.type === Box) {
            return child;
          }
          if (child.type === Typography) {
            const statusText = child.props.children;
            if (statusText === "DELIVERED") {
              return (
                <Typography key={index} variant="body2" sx={{ display: "inline-block", color: "#28a745" }}>
                  {statusText}
                </Typography>
              );
            } else if (statusText === "SHIPPED") {
              return (
                <Typography key={index} variant="body2" sx={{ display: "inline-block", color: "#5dabff" }}>
                  {statusText}
                </Typography>
              );
            } else if (statusText === "PROCESSING") {
              return (
                <Typography key={index} variant="body2" sx={{ display: "inline-block", color: "#fd7e14" }}>
                  {statusText}
                </Typography>
              );
            }else if (statusText === "CANCELED") {
              return (
                <Typography key={index} variant="body2" sx={{ display: "inline-block", color: "#dc3545" }}>
                  {statusText}
                </Typography>
              );
            }else if (statusText === "DELAYED") {
              return (
                <Typography key={index} variant="body2" sx={{ display: "inline-block", color: "#ffc107" }}>
                  {statusText}
                </Typography>
              );
            } else if (statusText === "PLACED") {
              return (
                <Typography key={index} variant="body2" sx={{ display: "inline-block", color: theme.palette.text.primary }}>
                  {statusText}
                </Typography>
              );
            }
            //CS-1556 - Removed specific color condition for pending status so that it uses default text color
          }
          return child;
          })}
    </Box>
      );
    };

    // CS-1555 - Start
    if (enableRipple) {
      return (
        <ButtonBase
          component={TableRow}
          hover
          role="checkbox"
          tabIndex={-1}
          key={row.uniqueId || rowIndex}
          onClick={(e) => handleTableRowClick(e, row)}
          {...rowHoverHandlers}
          sx={{
            ...commonRowSx,
            display: "table-row",
            width: "100%",
            borderRadius: 0,
            transition: "transform 0.1s ease-in-out",
            "&:active": { transform: "scale(0.998)" },
          }}
          disableRipple={false}
          TouchRippleProps={{
            style: {
              color:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.3)"
                  : "rgba(0,0,0,0.2)",
            },
          }}
          onMouseDown={(e: any) => {
            // Override row click on interactive elements
            const target = e.target as HTMLElement;
 
          }}
          // CS-1555 - End
      >
        {filteredColumns.map(
          (column) => (
            <TableCell
              key={column.id}
              align={column.align}
              className={stickyColumnIds.includes(column.id) ? "ct-sticky-cell" : undefined}
              sx={{
              ...(column.padding ? { padding: column.padding } : {}),
              ...getStickyCellSx(column.id, false),
              ...(stickyColumnIds.includes(column.id) ? { backgroundColor: stickyCellBg } : {}),
            }}>
              {(column.id === "status" || column.id === "ivrStatus") || column.id === "orderStatus" || column.id === "userStatus" || column.id === 'paymentStatus' ? (
              column.id === "orderStatus" ? (
                renderOrderStatus()
              ) : (
                <span
                style={{
                  color: isVerified || isPatientActive || isUserActive
                    ? "#1aa457"
                    : isDenied
                      ? "#cb0000"
                      : isDrafted
                        ? "#6f50a8"//"#e8c251" //cs-854 changes
                        : isNeedMoreInfo
                          ? "#e0a802"
                          : isCancelled || isVoid || isPatientClosed || isInactive
                            ? "#dc3545" 
                            : isPreAuth
                            ? "#17a2b8"
                            : "inherit",
                }}>
                {theme.palette.mode === "light" ? (
                    <>
                    <Typography style={{ color: "inherit" }}>
                   {row[column.id]}
                    </Typography>
                    </>
                  ) : (
                    row[column.id]
                  )}
                </span>
              )
            ) :
                // column.id === "practice" && row[column.id].length > 20 ? (   // CS-1984: guarded below
                column.id === "practice" && row[column.id] && row[column.id].length > 20 ? (   // CS-1984
                  <span title={row[column.id]}>{`${row[column.id].substring(
                    0,
                    20
                  )}...`}</span>
                ) : //CS-1571
                column.id === "description" && row[column.id] && row[column.id].length > 50 ? (
                  <span title={row[column.id]}>{`${row[column.id].substring(
                    0,
                    50
                  )}...`}</span>
                ) : column.format && typeof row[column.id] === "number" ? (
                  column.format(row[column.id])
                ) : column.id === "action" && row[column.id] ? (
                  showMenu ? (
                    <IconButton onClick={(event) => handleWithoutPropMenuClick(event, row[column.id], row)}>
                      <MoreVertIcon />
                    </IconButton>
                  ) : (
                    row[column.id]
                  )
                ) : column.id === "action" && hasActionComponent ? (
                  showMenu ? (
                    <IconButton onClick={(event) => handleMenuClick(event, row)}>
                      <MoreVertIcon />
                    </IconButton>
                  ) : (
                    actionComponent && actionComponent(row, rowIndex)
                  )
                ) : column.id === "isSizeActive" && availabilitySwitchComponent ? (
                  availabilitySwitchComponent(row, rowIndex)
                ) : column.id === "notificationEmail" ? (
                  <pre>{row[column.id]}</pre>
                ) : (
                  theme.palette.mode === "light" ? (
                    <>
                      <Typography>
                        {row[column.id]}
                      </Typography>
                    </>
                  ) : (
                    row[column.id]
                  )
                )}
            </TableCell>
          )
        )}
        </ButtonBase>
      );
    }
    // CS-1555 - Start
    // Fallback: Default TableRow
    return (
      <TableRow
        hover
        role="checkbox"
        tabIndex={-1}
        key={row.uniqueId || rowIndex}
        onClick={(e) => handleTableRowClick(e, row)}
        {...rowHoverHandlers}
        sx={commonRowSx}
      >
        {filteredColumns.map((column) => (
          <TableCell
            key={column.id}
            align={column.align}
            className={stickyColumnIds.includes(column.id) ? "ct-sticky-cell" : undefined}
            sx={{
            ...(column.padding ? { padding: column.padding } : {}),
            ...getStickyCellSx(column.id, false),
            ...(stickyColumnIds.includes(column.id) ? { backgroundColor: stickyCellBg } : {}),
          }}>
            {(column.id === "status" || column.id === "ivrStatus") || column.id === "orderStatus" || column.id === "userStatus" || column.id === 'paymentStatus' ? (
              column.id === "orderStatus" ? (
                renderOrderStatus()
              ) : (
                <span
                  style={{
                    color: isVerified || isPatientActive || isUserActive
                      ? "#1aa457"
                      : isDenied
                        ? "#cb0000"
                        : isDrafted
                          ? "#6f50a8"//"#e8c251" //cs-854 changes
                          : isNeedMoreInfo
                            ? "#e0a802"
                            : isCancelled || isVoid || isPatientClosed || isInactive
                              ? "#dc3545"
                              : isPreAuth
                                ? "#17a2b8"
                                : "inherit",
                  }}>
                  {theme.palette.mode === "light" ? (
                    <>
                      <Typography style={{ color: "inherit" }}>
                        {row[column.id]}
                      </Typography>
                    </>
                  ) : (
                    row[column.id]
                  )}
                </span>
              )
            ) :
              // column.id === "practice" && row[column.id].length > 20 ? (   // CS-1984: guarded below
              column.id === "practice" && row[column.id] && row[column.id].length > 20 ? (   // CS-1984
                <span title={row[column.id]}>{`${row[column.id].substring(
                  0,
                  20
                )}...`}</span>
              ) : //CS-1571
              column.id === "description" && row[column.id] && row[column.id].length > 50 ? (
                <span title={row[column.id]}>{`${row[column.id].substring(
                  0,
                  50
                )}...`}</span>
              ) : column.format && typeof row[column.id] === "number" ? (
                column.format(row[column.id])
              ) : column.id === "action" && row[column.id] ? (
                showMenu ? (
                  <IconButton onClick={(event) => handleWithoutPropMenuClick(event, row[column.id], row)}>
                    <MoreVertIcon />
                  </IconButton>
                ) : (
                  row[column.id]
                )
              ) : column.id === "action" && hasActionComponent ? (
                showMenu ? (
                  <IconButton onClick={(event) => handleMenuClick(event, row)}>
                    <MoreVertIcon />
                  </IconButton>
                ) : (
                  actionComponent && actionComponent(row, rowIndex)
                )
              ) : column.id === "isSizeActive" && availabilitySwitchComponent ? (
                availabilitySwitchComponent(row, rowIndex)
              ) : column.id === "notificationEmail" ? (
                <pre>{row[column.id]}</pre>
              ) : (
                theme.palette.mode === "light" ? (
                  <>
                    <Typography>
                      {row[column.id]}
                    </Typography>
                  </>
                ) : (
                  row[column.id]
                )
              )}
          </TableCell>
        ))}
      </TableRow>
      // CS-1555 - End
    );
  };

  const IconBox = styled(Box)({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 32,
    height: 32,
    borderRadius: "6px",
    border: `1px dotted ${colors.grey[700]}`,
    color: colors.grey[600],
    fontWeight: "bold",
    fontSize: "14px",
  });

  const DraftedChip = styled(Chip)({
    fontWeight: "bold",
    borderRadius: "20px",
    fontSize: "12px",
    height: 30,
    border: "1px solid",
    backgroundColor: colors.grey[900],
  });

  const FieldContainer = styled(Box)({
    display: "flex",
    alignItems: "center",
    paddingBottom: 8,
    borderBottom: `1px dotted ${colors.grey[500]}`,
    "&:last-child": {
      borderBottom: "none",
    },
  });

  const renderCardView = (row: Row, rowIndex: number) => {
    const isDrafted = row.status === IVR_STATUS.DRAFTED;
    const isCancelled = row.status === IVR_STATUS.CANCELED;
    const isNeedMoreInfo = row.status === IVR_STATUS.NEEDS_MORE_INFO;

    const isSelected = activeMenuRow === row;
    const isActiveMenuRow = activeMenuRow && uniqueValue && activeMenuRow[uniqueValue] === row[uniqueValue];
    // CS-1555 - Start
    const isHighlightedRow = (() => {
      if (!uniqueValue) return false;
      const activeIdForPage = getSelectionForPage(currentPageKey || "default");
      if (!activeIdForPage) return false;
      const rowId = String(row[uniqueValue]);
      const activeId = activeIdForPage.split("-").pop();
      return rowId === activeId;
    })();

    // const rowKey = uniqueValue ? String(row[uniqueValue]) : String(rowIndex);
    const hasActionComponent = actionComponent && actionComponent(row, rowIndex) !== null;

    const handleCardClick = async(event: React.MouseEvent, row: Row) => {

      if (openMenuOnRowClick && showMenu && actionComponent) {
        handleMenuClick(event as unknown as React.MouseEvent<HTMLElement>, row);
        return;
      }

      if (onRowClick && !disableRowClickOnCards && clickableCards) {
        await new Promise(resolve => setTimeout(resolve, 180));
        handleRowClick(row);
        onRowClick(row);
      }
    };
    // CS-1555 - End

    // Card content wrapper
    const cardContent = ( // CS-1555
      <Box
        sx={{
          backgroundColor: colors.grey[900],
          borderRadius: 3,
          border: 1,
          borderColor: colors.primary[500],
          width: '100%',
        }}
      >
        {filteredColumns.map((column, index) => (
          <Box key={index} sx={{ position: "relative" }}>
            {column.id === "action" && row[column.id] ? (
              <IconButton
                sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
                onClick={(event) => {
                  event.stopPropagation(); // CS-1555
                  handleWithoutPropMenuClick(event, row[column.id], row);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            ) : column.id === "action" && hasActionComponent ? (
              <IconButton
                sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
                onClick={(event) => {
                  event.stopPropagation(); // CS-1555
                  handleMenuClick(event, row);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            ) : null}
          </Box>
        ))}
        <Card
          sx={{
            backgroundColor: isHighlightedRow
              ? colors.grey[600]
              : (isSelected || isActiveMenuRow ? colors.grey[700] : "transparent"),
            overflowWrap: "break-word",
            wordBreak: "break-word",
            maxWidth: "100%",
          }}
        // sx={{ backgroundColor: isSelected || isActiveMenuRow ? colors.grey[700] : "inherit", }}
        //CS-1294 - End
        >

          {(filteredColumns[0].id !== "action" &&
            !filteredColumns[0].id.includes("checkbox")) && (
              <Box
                display="flex"
                alignItems="center"
                pb={1}
                borderBottom={`1px dotted ${theme.palette.grey[500]}`}
              >
                <IconBox>
                  <RadioButtonChecked />
                </IconBox>
                <Box ml={2}>
                  <Typography
                    sx={{ color: colors.grey[500], fontSize: "0.7em" }}
                  >
                    {filteredColumns[0].id !== "action" &&
                      filteredColumns[0].label}
                  </Typography>
                  {filteredColumns[0].id !== "action" &&
                    row[filteredColumns[0].id]}
                </Box>
              </Box>
            )}
          <Stack spacing={1.5} mt={1}>
            {filteredColumns.slice(1).map(
              (column, index) =>
                column.id !== "action" && (
                  <FieldContainer key={index}>
                    <IconBox>
                      <RadioButtonChecked />
                    </IconBox>
                    <Box ml={2}>
                      <Typography
                        sx={{ color: colors.grey[500], fontSize: "0.7em" }}
                      >
                        {column.label}
                      </Typography>

                      {column.id.toLowerCase().includes("status") ? (
                        <DraftedChip
                          label={row[column.id]}
                          variant="outlined"
                          sx={{
                            color: isDrafted
                              ? "#d95c16"
                              : isNeedMoreInfo
                                ? "orange"
                                : isCancelled
                                  ? "#7b3fce"
                                  : colors.success[400],
                          }}
                        />
                      ) : column.id === "isSizeActive" && availabilitySwitchComponent ? (
                        availabilitySwitchComponent(row, rowIndex)
                      ) : (
                        row[column.id]
                      )}
                    </Box>
                  </FieldContainer>
                )
            )}
          </Stack>
        </Card>
      </Box>
    );
    // CS-1555 - Start
    return (
      <Box key={rowIndex} sx={{ mb: 2 }}>
        {clickableCards && onRowClick && !disableRowClickOnCards ? (
          <ButtonBase
            component="div"
            onClick={(e) => handleCardClick(e, row)}
            sx={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              borderRadius: 3,
              overflow: 'hidden',
              transition: 'transform 0.15s ease-in-out, opacity 0.15s ease-in-out',
              '&:active': {
                transform: 'scale(0.95)',
                opacity: 0.7,
              },
            }}
            disableRipple={false}
            TouchRippleProps={{
              style: {
                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)',
              }
            }}
            // Prevent ButtonBase from blocking child clicks
            onMouseDown={(e) => {
              // Check if click is on an interactive element
              const target = e.target as HTMLElement;
              const isInteractive = !!target.closest(
                'button, a, input, select, textarea, [role="button"], [role="menuitem"], ' +
                '[role="link"], [role="checkbox"], [role="switch"], ' +
                '.MuiIconButton-root, .MuiCheckbox-root, .MuiSwitch-root, .MuiChip-root[onclick]'
              );
              if (isInteractive) e.stopPropagation();
            }}
          >
            {cardContent}
          </ButtonBase>
        ) : (
          <Box
            onClick={(e) => handleCardClick(e, row)}
            sx={{
              cursor: onRowClick && !disableRowClickOnCards ? "pointer" : "default",
            }}
          >
            {cardContent}
          </Box>
        )}
      </Box>
    );
  };
  //CS-1555 - End

  const renderMobileCards = () => (
    <Box sx={{width: "100%", ...sx}}>
      {sortedRows.length > 0 ? (
        sortedRows.map((row, rowIndex) => renderCardView(row, rowIndex))
      ) : (
        <Box sx={{ textAlign: "center", padding: 4, px: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            {emptyText || "No Data Found"}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderPagination = () => (
    <Box
      sx={{
        padding: 1,
        bottom: 0,
        background: colors.grey[900],
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md>
          <Box display="flex" justifyContent="center">
            <Pagination
              variant="outlined"
              shape="rounded"
              count={totalPages || Math.ceil(rows.length / rowsPerPage)}
              page={page + 1}
              onChange={(event, value) => handleChangePage(event, value - 1)}
              color={theme.palette.mode === "dark" ? "secondary" : "primary"}
              showFirstButton
              showLastButton
            />
          </Box>
        </Grid>
        <Grid item>
          <Box display="flex" justifyContent="flex-end">
            <label style={{ textWrap: "nowrap", fontSize: "13px", alignSelf: "center" }}>{totalElementText ? totalElementText : ""}</label>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  const renderMenuItems = () => {
    if (!menuRow || !actionComponent) return null;
    // CS-1555 - Start
    const sourceRow = (menuRow as any)?.originalOrder ?? menuRow;
    const actions = actionComponent(sourceRow, rows.indexOf(menuRow));
    const toggleSwitch = availabilitySwitchComponent ? availabilitySwitchComponent(sourceRow, 0) : null;
    // CS-1555 - End
    const renderComponentItems = (component: JSX.Element) =>
      React.Children.map(component, (action, index) =>
        React.Children.map(action.props?.children, (child, childIndex) => {
          if (!child?.props || child.props?.style?.display === 'none') return null;

          const { onClick, tooltipMessage, disabled } = child.props; // CS-1461

          return (
            <MenuItem
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: 1.5,
                mx: 0.75,
                my: 0.35,
                px: 1.1,
                py: 0.75,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: theme.palette.mode === "dark"
                    ? "rgba(20,184,166,0.14)"
                    : "rgba(20,184,166,0.08)",
                },
              }}
              onClick={() => {
                // CS-1461 - Start
                if (!disabled) {
                  onClick?.();
                  handleMenuClose();
                }
                // CS-1461 - End
              }}
            >
              {child}
              {tooltipMessage && (
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {tooltipMessage}
                </Typography>
              )}
            </MenuItem>
          );
        })
      );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {actions && renderComponentItems(actions)}
        {toggleSwitch && renderComponentItems(toggleSwitch)}
      </Box>
    );
  };

  const renderWithoutPropsMenuItems = () => {
    const actionComponent_ = menuActionPropRow || null;
    return (
      <>
        {React.Children.map(actionComponent_, (action, index) =>
          React.Children.map(action.props?.children, (child, childIndex) => {
            if (!child?.props || child.props?.style?.display === "none")
              return null;

            const { onClick, tooltipMessage } = child.props;

            return (
              <MenuItem
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 1.5,
                  mx: 0.75,
                  my: 0.35,
                  px: 1.1,
                  py: 0.75,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: theme.palette.mode === "dark"
                      ? "rgba(20,184,166,0.14)"
                      : "rgba(20,184,166,0.08)",
                  },
                }}
                onClick={() => {
                  onClick?.();
                  handleMenuClose();
                }}
              >
                {child}
                {tooltipMessage && (
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {tooltipMessage}
                  </Typography>
                )}
              </MenuItem>
            );
          })
        )}
      </>
    );
  };
  
  const tableBorderStyle = !isMobile ? {
    backgroundColor: (theme: any) =>
      theme.palette.mode === "dark"
        ? tokens(theme.palette.mode).primary[500]
        : tokens(theme.palette.mode).grey[700],
    borderRadius: 2,
    p: 1,
  } : {};

  return (
    <Box sx={{ ...(borderBox ? tableBorderStyle : {}), ...(sx || {}) }}>
      {renderTable()}
      <Menu
        // CS-1555 - Start
        anchorEl={menuPosition ? undefined : anchorEl}
        anchorReference={menuPosition ? "anchorPosition" : "anchorEl"}
        anchorPosition={menuPosition ? { top: menuPosition.top, left: menuPosition.left } : undefined}
        open={Boolean(anchorEl) || Boolean(menuPosition)}
        // CS-1555 - End
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.6,
            minWidth: 170,
            borderRadius: 2.5,
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(148,163,184,0.18)" : "rgba(148,163,184,0.28)"}`,
            backgroundColor: theme.palette.mode === "dark" ? "#141720" : "#ffffff",
            boxShadow: theme.palette.mode === "dark"
              ? "0 14px 36px rgba(2,6,23,0.55)"
              : "0 14px 32px rgba(15,23,42,0.12)",
            overflow: "hidden",
          },
        }}
        MenuListProps={{
          sx: {
            py: 0.7,
          },
        }}
      >
        {isActionProps ? renderMenuItems() : renderWithoutPropsMenuItems()}
      </Menu>
    </Box>
  );
};

export default CustomTable;